class BlankaboxExperience extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.hero = this.querySelector('[data-bbx-hero]');
    this.scrollCanvas = this.querySelector('[data-bbx-scroll-canvas]');
    this.canvasContext = this.scrollCanvas?.getContext('2d', { alpha: false });
    this.weaveCanvas = this.querySelector('[data-bbx-weave-canvas]');
    this.weaveContext = this.weaveCanvas?.getContext('2d');
    this.frameSourceNode = this.querySelector('[data-bbx-frame-sources]');
    this.boxReveal = this.querySelector('[data-bbx-box-reveal]');
    this.revealItems = [...this.querySelectorAll('[data-bbx-reveal]')];
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.frame = null;

    this.updateHero = this.updateHero.bind(this);
    this.requestHeroUpdate = this.requestHeroUpdate.bind(this);
    this.handleEditionMediaClick = this.handleEditionMediaClick.bind(this);
    this.handleFabricInteraction = this.handleFabricInteraction.bind(this);
    this.animateFabricRail = this.animateFabricRail.bind(this);

    this.setupReveals();
    this.setupFrameSequence();
    this.setupFabricRail();
    this.addEventListener('click', this.handleEditionMediaClick);

    if (!this.reducedMotion.matches) {
      window.addEventListener('scroll', this.requestHeroUpdate, { passive: true });
      window.addEventListener('resize', this.requestHeroUpdate, { passive: true });

      this.updateHero();
    } else {
      if (this.boxReveal) {
        this.boxReveal.style.opacity = '1';
      }
    }
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.requestHeroUpdate);
    window.removeEventListener('resize', this.requestHeroUpdate);
    this.removeEventListener('click', this.handleEditionMediaClick);
    this.fabricRail?.removeEventListener('pointerdown', this.handleFabricInteraction);
    this.fabricRail?.removeEventListener('pointerup', this.handleFabricInteraction);
    this.fabricRail?.removeEventListener('pointercancel', this.handleFabricInteraction);
    this.fabricRail?.removeEventListener('touchstart', this.handleFabricInteraction);
    this.fabricRail?.removeEventListener('touchend', this.handleFabricInteraction);
    this.fabricRail?.removeEventListener('wheel', this.handleFabricInteraction);
    this.fabricVisibilityObserver?.disconnect();
    this.revealObserver?.disconnect();
    if (this.frame) cancelAnimationFrame(this.frame);
    if (this.fabricAutoFrame) cancelAnimationFrame(this.fabricAutoFrame);
  }

  setupReveals() {
    if (this.reducedMotion.matches || !('IntersectionObserver' in window)) {
      this.revealItems.forEach((item) => item.classList.add('is-visible'));
      return;
    }

    this.revealObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.12 }
    );

    this.revealItems.forEach((item) => this.revealObserver.observe(item));
  }

  requestHeroUpdate() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.updateHero();
      this.frame = null;
    });
  }

  handleEditionMediaClick(event) {
    if (window.innerWidth > 749) return;

    const media = event.target.closest('[data-bbx-edition-media]');
    if (!media || !this.contains(media)) return;

    const isAlternateVisible = media.classList.toggle('is-alternate-visible');
    media.setAttribute('aria-pressed', String(isAlternateVisible));
  }

  setupFabricRail() {
    this.fabricRail = this.querySelector('[data-bbx-fabric-rail]');
    if (!this.fabricRail || this.reducedMotion.matches) return;

    this.fabricDirection = 1;
    this.fabricAutoPosition = this.fabricRail.scrollLeft;
    this.fabricWasPaused = true;
    this.fabricVelocity = 0;
    this.fabricIsVisible = true;
    this.fabricPausedUntil = performance.now() + 1200;
    this.fabricLastTimestamp = null;
    this.fabricRail.addEventListener('pointerdown', this.handleFabricInteraction, { passive: true });
    this.fabricRail.addEventListener('pointerup', this.handleFabricInteraction, { passive: true });
    this.fabricRail.addEventListener('pointercancel', this.handleFabricInteraction, { passive: true });
    this.fabricRail.addEventListener('touchstart', this.handleFabricInteraction, { passive: true });
    this.fabricRail.addEventListener('touchend', this.handleFabricInteraction, { passive: true });
    this.fabricRail.addEventListener('wheel', this.handleFabricInteraction, { passive: true });

    if ('IntersectionObserver' in window) {
      this.fabricIsVisible = false;
      this.fabricVisibilityObserver = new IntersectionObserver(
        ([entry]) => {
          this.fabricIsVisible = entry?.isIntersecting === true;
        },
        { rootMargin: '120px 0px' }
      );
      this.fabricVisibilityObserver.observe(this.fabricRail);
    }

    this.fabricAutoFrame = requestAnimationFrame(this.animateFabricRail);
  }

  handleFabricInteraction(event) {
    const interactionStarted = event.type === 'pointerdown' || event.type === 'touchstart';
    this.fabricPausedUntil = interactionStarted ? Number.POSITIVE_INFINITY : performance.now() + 1800;
    this.fabricAutoPosition = this.fabricRail?.scrollLeft || 0;
    this.fabricWasPaused = true;
    this.fabricVelocity = 0;
  }

  animateFabricRail(timestamp) {
    if (!this.fabricRail) return;

    if (this.fabricLastTimestamp === null) this.fabricLastTimestamp = timestamp;
    const elapsed = Math.min(timestamp - this.fabricLastTimestamp, 40);
    this.fabricLastTimestamp = timestamp;

    const fabricInteracting = this.fabricRail.matches(':hover') || this.fabricRail.contains(document.activeElement) || this.fabricRail.querySelector('.is-model-visible');
    if (this.fabricIsVisible && !fabricInteracting && timestamp >= this.fabricPausedUntil) {
      const maxScroll = Math.max(this.fabricRail.scrollWidth - this.fabricRail.clientWidth, 0);
      const targetSpeed = window.innerWidth <= 749 ? 34 : 40;

      if (maxScroll > 0) {
        if (this.fabricWasPaused) {
          this.fabricAutoPosition = this.fabricRail.scrollLeft;
          this.fabricWasPaused = false;
        }

        const acceleration = Math.min(elapsed / 850, 1);
        this.fabricVelocity += (targetSpeed - this.fabricVelocity) * acceleration;
        this.fabricAutoPosition += this.fabricDirection * this.fabricVelocity * (elapsed / 1000);

        if (this.fabricAutoPosition >= maxScroll) {
          this.fabricAutoPosition = maxScroll;
          this.fabricDirection = -1;
          this.fabricVelocity = 0;
        } else if (this.fabricAutoPosition <= 0) {
          this.fabricAutoPosition = 0;
          this.fabricDirection = 1;
          this.fabricVelocity = 0;
        }

        this.fabricRail.scrollLeft = this.fabricAutoPosition;
      }
    } else {
      this.fabricWasPaused = true;
      this.fabricVelocity = 0;
    }

    this.fabricAutoFrame = requestAnimationFrame(this.animateFabricRail);
  }

  setupFrameSequence() {
    if (!this.frameSourceNode || !this.scrollCanvas || !this.canvasContext) return;

    try {
      this.frameSources = JSON.parse(this.frameSourceNode.textContent);
    } catch (error) {
      this.frameSources = [];
    }

    if (!Array.isArray(this.frameSources) || this.frameSources.length === 0) return;

    this.sequenceFrames = new Array(this.frameSources.length);
    this.targetFrameIndex = 0;

    this.frameSources.forEach((source, index) => {
      const frame = new Image();
      frame.decoding = 'async';
      frame.onload = () => {
        if (index === this.targetFrameIndex) this.drawSequenceFrame(frame, index);
      };
      frame.src = source;
      this.sequenceFrames[index] = frame;
    });
  }

  updateSequence(progress) {
    if (!this.sequenceFrames?.length) return;

    const frameIndex = Math.round(progress * (this.sequenceFrames.length - 1));
    this.targetFrameIndex = frameIndex;
    const frame = this.sequenceFrames[frameIndex];

    if (frame?.complete && frame.naturalWidth > 0) {
      this.drawSequenceFrame(frame, frameIndex);
    }
  }

  drawSequenceFrame(frame, frameIndex) {
    if (!frame || !this.scrollCanvas || !this.canvasContext) return;

    const bounds = this.scrollCanvas.getBoundingClientRect();
    if (!bounds.width || !bounds.height) return;

    const pixelRatio = Math.min(window.devicePixelRatio || 1, 1.5);
    const outputWidth = Math.round(bounds.width * pixelRatio);
    const outputHeight = Math.round(bounds.height * pixelRatio);
    const dimensionsChanged = this.scrollCanvas.width !== outputWidth || this.scrollCanvas.height !== outputHeight;

    if (this.renderedFrameIndex === frameIndex && !dimensionsChanged) return;

    if (dimensionsChanged) {
      this.scrollCanvas.width = outputWidth;
      this.scrollCanvas.height = outputHeight;
    }

    const frameRatio = frame.naturalWidth / frame.naturalHeight;
    const canvasRatio = outputWidth / outputHeight;
    let sourceWidth = frame.naturalWidth;
    let sourceHeight = frame.naturalHeight;
    let sourceX = 0;
    let sourceY = 0;

    if (frameRatio > canvasRatio) {
      sourceWidth = frame.naturalHeight * canvasRatio;
      sourceX = (frame.naturalWidth - sourceWidth) / 2;
    } else {
      sourceHeight = frame.naturalWidth / canvasRatio;
      sourceY = (frame.naturalHeight - sourceHeight) / 2;
    }

    this.canvasContext.drawImage(
      frame,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      0,
      0,
      outputWidth,
      outputHeight
    );
    this.renderedFrameIndex = frameIndex;
    this.scrollCanvas.classList.add('is-ready');
  }

  updateHero() {
    if (!this.hero) return;

    const bounds = this.hero.getBoundingClientRect();
    const scrollableDistance = Math.max(this.hero.offsetHeight - window.innerHeight, 1);
    const rawProgress = Math.min(Math.max(-bounds.top / scrollableDistance, 0), 1);
    const mobileIntroHold = window.innerWidth <= 749 ? 0.15 : 0;
    const progress = Math.min(Math.max((rawProgress - mobileIntroHold) / (1 - mobileIntroHold), 0), 1);
    const sequenceProgress = Math.min(Math.max((progress - 0.01) / 0.52, 0), 1);
    const weaveProgress = Math.min(Math.max((progress - 0.02) / 0.62, 0), 1);
    const boxProgress = Math.min(Math.max((progress - 0.56) / 0.34, 0), 1);
    const introFade = Math.min(Math.max((progress - 0.04) / 0.1, 0), 1);
    const secondIntroEnter = Math.min(Math.max((progress - 0.18) / 0.08, 0), 1);
    const secondIntroExit = Math.min(Math.max((progress - 0.44) / 0.08, 0), 1);
    const secondIntroOpacity = secondIntroEnter * (1 - secondIntroExit);
    const revealCopy = Math.min(Math.max((boxProgress - 0.28) / 0.48, 0), 1);
    const boxEase = boxProgress * boxProgress * (3 - 2 * boxProgress);
    const mediaExpansion = Math.min(Math.max((weaveProgress - 0.5) / 0.42, 0), 1);
    const expansionEase = mediaExpansion * mediaExpansion * (3 - 2 * mediaExpansion);
    const baseMediaInset = window.innerWidth <= 749 ? 10 : 28;
    const baseMediaRadius = window.innerWidth <= 749 ? 20 : 30;

    this.style.setProperty('--bbx-progress', progress.toFixed(4));
    this.style.setProperty('--bbx-intro-opacity', (1 - introFade).toFixed(3));
    this.style.setProperty('--bbx-second-opacity', secondIntroOpacity.toFixed(3));
    this.style.setProperty('--bbx-copy-opacity', revealCopy.toFixed(3));
    this.style.setProperty('--bbx-live-media-inset', `${(baseMediaInset * (1 - expansionEase)).toFixed(2)}px`);
    this.style.setProperty('--bbx-live-media-radius', `${(baseMediaRadius * (1 - expansionEase)).toFixed(2)}px`);
    this.style.setProperty('--bbx-box-scale', (1.035 - boxEase * 0.035).toFixed(4));
    this.classList.toggle('is-copy-visible', revealCopy > 0.08);

    this.updateSequence(sequenceProgress);
    this.updateWeave(weaveProgress);
    this.updateBoxReveal(boxProgress);
  }

  updateWeave(progress) {
    if (!this.weaveCanvas || !this.weaveContext) return;

    const bounds = this.weaveCanvas.getBoundingClientRect();
    if (!bounds.width || !bounds.height) return;

    const pixelRatio = Math.min(window.devicePixelRatio || 1, 1.5);
    const outputWidth = Math.round(bounds.width * pixelRatio);
    const outputHeight = Math.round(bounds.height * pixelRatio);

    if (this.weaveCanvas.width !== outputWidth || this.weaveCanvas.height !== outputHeight) {
      this.weaveCanvas.width = outputWidth;
      this.weaveCanvas.height = outputHeight;
    }

    const context = this.weaveContext;
    context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    context.clearRect(0, 0, bounds.width, bounds.height);
    if (progress <= 0) return;

    const smooth = progress * progress * (3 - 2 * progress);
    const solidWhite = Math.min(Math.max((progress - 0.58) / 0.42, 0), 1);
    context.fillStyle = `rgba(247, 244, 238, ${(smooth * 0.34 + solidWhite * 0.66).toFixed(3)})`;
    context.fillRect(0, 0, bounds.width, bounds.height);

    const warpProgress = Math.min(progress / 0.78, 1);
    const weftProgress = Math.min(Math.max((progress - 0.05) / 0.86, 0), 1);
    const warpSpacing = window.innerWidth <= 749 ? 8 : 10;
    const weftSpacing = window.innerWidth <= 749 ? 7 : 9;
    const totalWarp = Math.ceil(bounds.width / warpSpacing) + 4;
    const totalWeft = Math.ceil(bounds.height / weftSpacing) + 4;
    const visibleWarp = Math.ceil(totalWarp * warpProgress);
    const visibleWeft = Math.ceil(totalWeft * weftProgress);
    const centerWarp = Math.floor(totalWarp / 2);

    context.lineCap = 'round';

    for (let index = 0; index < visibleWarp; index += 1) {
      const direction = index % 2 === 0 ? 1 : -1;
      const distance = Math.ceil(index / 2);
      const warpIndex = centerWarp + direction * distance;
      const x = (warpIndex - 2) * warpSpacing;
      const wave = 0.55 + (index % 3) * 0.18;

      context.beginPath();
      context.moveTo(x, 0);
      for (let y = 0; y <= bounds.height + 16; y += 16) {
        context.lineTo(x + Math.sin(y * 0.035 + index * 0.7) * wave, y);
      }
      context.strokeStyle = `rgba(91, 84, 74, ${(0.035 + smooth * 0.035).toFixed(3)})`;
      context.lineWidth = 2.2;
      context.stroke();
      context.strokeStyle = `rgba(255, 254, 251, ${(0.62 + smooth * 0.28).toFixed(3)})`;
      context.lineWidth = 1.15;
      context.stroke();
    }

    for (let index = 0; index < visibleWeft; index += 1) {
      const y = (index - 2) * weftSpacing;
      const wave = 0.5 + (index % 4) * 0.14;

      context.beginPath();
      context.moveTo(0, y);
      for (let x = 0; x <= bounds.width + 18; x += 18) {
        context.lineTo(x, y + Math.sin(x * 0.032 + index * 0.82) * wave);
      }
      context.strokeStyle = `rgba(87, 80, 71, ${(0.045 + smooth * 0.04).toFixed(3)})`;
      context.lineWidth = 2;
      context.stroke();
      context.strokeStyle = `rgba(255, 255, 253, ${(0.68 + smooth * 0.26).toFixed(3)})`;
      context.lineWidth = 1;
      context.stroke();
    }
  }

  updateBoxReveal(progress) {
    if (!this.boxReveal) return;

    if (progress <= 0) {
      this.boxReveal.style.opacity = '0';
      this.boxReveal.style.clipPath = 'polygon(50% 0, 50% 0, 50% 100%, 50% 100%)';
      return;
    }

    const eased = progress * progress * (3 - 2 * progress);
    const yPositions = [0, 11, 23, 36, 49, 62, 75, 88, 100];
    const leftTargets = [-3, 1, -2, 0, -4, 1, -1, 0, -3];
    const rightTargets = [103, 99, 102, 100, 104, 99, 101, 100, 103];
    const leftEdge = yPositions.map((y, index) => {
      const x = 50 + (leftTargets[index] - 50) * eased;
      return `${x.toFixed(2)}% ${y}%`;
    });
    const rightEdge = yPositions
      .map((y, index) => {
        const x = 50 + (rightTargets[index] - 50) * eased;
        return `${x.toFixed(2)}% ${y}%`;
      })
      .reverse();

    this.boxReveal.style.opacity = Math.min(progress * 8, 1).toFixed(3);
    this.boxReveal.style.clipPath = `polygon(${[...leftEdge, ...rightEdge].join(', ')})`;
  }
}

if (!customElements.get('blankabox-experience')) {
  customElements.define('blankabox-experience', BlankaboxExperience);
}
